import { SystemError } from "framework/error_handling/system-error";
import { Archivable, Creatable, Modifiable, UserInfo } from "model/business-base";
import { ClassOfTravel, DeviationDetailType, DeviationRequestStatus, DeviationType, DeviationWorkflowType, FlightTime } from "model/business-enums";
import { Airline, City, Country, DeviationTeamRegionDirector, OrganisationEntity } from "model/business-keywords";
import { UserContact } from "model/business-types";
import { EntityBase, EnumInfo } from "model/platform-types";
import { UploadedFile } from "model/essential-types";
import { SystemUserWrapper } from "model/support-types";

export interface DeviationRequestBase extends EntityBase, Creatable, Modifiable, Archivable {
  deviationType: DeviationType;
  workflowType: DeviationWorkflowType;
  status: DeviationRequestStatus;
  statusChangedOn: string | null;
  statusChangedBy: UserInfo;
  requestedBy: UserContact;
  organisationEntity: OrganisationEntity;
  validFrom: string | null;
  validTo: string | null;
  detail: DeviationDetail;
  epra: UserInfo | null;
  isSupportedByEpra: boolean;
  regionDirector: DeviationTeamRegionDirector | null;
  reason: string;
  alternativeOptions: string;
  riskManagement: string;
  additionalInfo: string | null;
  assignedOn: string | null;
  assignedTo: UserInfo | null;
  attachments: UploadedFile[];
}

export interface SingleInstanceDeviationRequest extends DeviationRequestBase {
  lineManagerName: string;
  isSupportedByLineManager: boolean;
  travellers: SystemUserWrapper[];
}

export interface EntityWideDeviationRequest extends DeviationRequestBase {
  regionalEntityLeader: UserContact;
  isSupportedByRegionalEntityLeader: boolean;
  sORLeader: UserContact | null;
  isSupportedBySORLeader: boolean;
  aviationCOE: UserContact | null;
  isSupportedByAviationCOE: boolean | null;
  seniorVPHSE: UserContact | null;
  isSupportedBySeniorVPHSE: boolean | null;
  cfo: UserContact | null;
  isSupportedByCFO: boolean | null;
}

export type DeviationRequest = SingleInstanceDeviationRequest | EntityWideDeviationRequest;

export interface DeviationRequestWorkflowStrategy {
  hasAviationCOE: boolean;
  hasSeniorVPHSE: boolean;
  hasCFO: boolean;
  hasEpra: boolean;
  hasSORLeader: boolean;
  hasRegionDirector: boolean;
  hasLineManager: boolean;
}

export interface DeviationDetailBase {
  detailType: DeviationDetailType;
}

export interface AirlineDeviationDetail extends DeviationDetailBase {
  airline: Airline | null;
  airlineCustomName: string | null;
}

export interface ClassOfTravelDeviationDetail extends DeviationDetailBase {
  airline: Airline | null;
  airlineCustomName: string | null;
  originCity: City | null;
  originCityCustomName: string | null;
  originCountry: Country | null;
  destinationCity: City | null;
  destinationCityCustomName: string | null;
  destinationCountry: Country | null;
  classOfTravel: EnumInfo<ClassOfTravel> | null;
  flightTime: EnumInfo<FlightTime> | null;
}

export interface HotelDeviationDetail extends DeviationDetailBase {
  hotelName: string | null;
  city: City | null;
  cityCustomName: string | null;
  country: Country | null;
}

export interface AirlineExtendedDeviationDetail extends DeviationDetailBase {
  airline: Airline | null;
  airlineCustomName: string | null;
  originCity: City | null;
  originCityCustomName: string | null;
  originCountry: Country | null;
  destinationCity: City | null;
  destinationCityCustomName: string | null;
  destinationCountry: Country | null;
}

export type DeviationDetail = AirlineDeviationDetail | ClassOfTravelDeviationDetail | HotelDeviationDetail | AirlineExtendedDeviationDetail;

export interface DeviationSettings {
  deviationType: DeviationType | null;
  workflowType: DeviationWorkflowType | null;
  detailType: DeviationDetailType | null;
  prepopulatedFields: DeviationSettingsPrepopulatedFields | null;
}

export interface DeviationSettingsPrepopulatedFields {
  airline: Airline | null;
  airlineCustomName: string | null;
  validFrom: string;
  validTo: string;
  travelers: UserContact[];
}

export function initDeviationRequest(initialDeviationSettings: DeviationSettings, currentUser: UserContact): DeviationRequest {
  let detail: DeviationDetail = null;
  switch (initialDeviationSettings.detailType) {
    case DeviationDetailType.Airline:
      detail = { detailType: DeviationDetailType.Airline, airline: null, airlineCustomName: null } as AirlineDeviationDetail;
      break;
    case DeviationDetailType.ClassOfTravel:
      detail = { detailType: DeviationDetailType.ClassOfTravel, airline: null, airlineCustomName: null, originCity: null, originCityCustomName: null, originCountry: null, destinationCity: null, destinationCityCustomName: null, destinationCountry: null, classOfTravel: null, flightTime: null } as ClassOfTravelDeviationDetail;
      break;
    case DeviationDetailType.Hotel:
      detail = { detailType: DeviationDetailType.Hotel, hotelName: null, city: null, cityCustomName: null, country: null } as HotelDeviationDetail;
      break;
    case DeviationDetailType.AirlineExtended:
      detail = {
        detailType: DeviationDetailType.AirlineExtended,
        airline: initialDeviationSettings.prepopulatedFields !== null ? initialDeviationSettings.prepopulatedFields.airline : null,
        airlineCustomName: initialDeviationSettings.prepopulatedFields !== null ? initialDeviationSettings.prepopulatedFields.airlineCustomName : null,
        originCity: null,
        originCityCustomName: null,
        originCountry: null,
        destinationCity: null,
        destinationCityCustomName: null,
        destinationCountry: null
      } as AirlineExtendedDeviationDetail;
      break;
    default:
      throw new SystemError(`Deviation detail type ${initialDeviationSettings.detailType} is not supported`, "Not supported");
  }
  const deviationRequestBase = {
    id: null,
    deviationType: initialDeviationSettings.deviationType,
    workflowType: initialDeviationSettings.workflowType,
    detail: detail,
    requestedBy: currentUser,
    organisationEntity: null,
    validFrom: initialDeviationSettings.prepopulatedFields !== null ? initialDeviationSettings.prepopulatedFields.validFrom : null,
    validTo: initialDeviationSettings.prepopulatedFields !== null ? initialDeviationSettings.prepopulatedFields.validTo : null,
    alternativeOptions: null,
    reason: null,
    riskManagement: null,
    additionalInfo: null,
    epra: null,
    isSupportedByEpra: false,
    regionDirector: null,
    status: DeviationRequestStatus.Pending,
    statusChangedOn: null,
    statusChangedBy: null,
    assignedOn: null,
    assignedTo: null,
    attachments: []
  } as DeviationRequestBase;
  switch (initialDeviationSettings.deviationType) {
    case DeviationType.SingleInstance:
      return {
        ...deviationRequestBase,
        lineManagerName: null,
        isSupportedByLineManager: false,
        travellers: initialDeviationSettings.prepopulatedFields !== null ? initialDeviationSettings.prepopulatedFields.travelers.map(x => ({ user: x, userId: null } as SystemUserWrapper )): [{ user: currentUser, userId: null } as SystemUserWrapper]
      } as SingleInstanceDeviationRequest;
    case DeviationType.EntityWide:
      return {
        ...deviationRequestBase,
        aviationCOE: null,
        isSupportedByAviationCOE: false,
        cfo: null,
        isSupportedByCFO: false,
        regionalEntityLeader: null,
        isSupportedByRegionalEntityLeader: false,
        seniorVPHSE: null,
        isSupportedBySeniorVPHSE: false,
        sORLeader: null,
        isSupportedBySORLeader: false
      } as EntityWideDeviationRequest;
    default:
      throw new SystemError(`Deviation type ${initialDeviationSettings.deviationType} is not supported`, "Not supported");
  }
}

export function copyDeviationRequest(copyFromRequest: DeviationRequest, currentUser: UserContact): DeviationRequest {
  let detail: DeviationDetail = null;
  switch (copyFromRequest.detail.detailType) {
    case DeviationDetailType.Airline: {
      const airlineDetail = copyFromRequest.detail as AirlineDeviationDetail
      detail = { detailType: DeviationDetailType.Airline, airline: airlineDetail.airline, airlineCustomName: airlineDetail.airlineCustomName } as AirlineDeviationDetail;
      break;
    }
    case DeviationDetailType.ClassOfTravel: {
      const classOfTravelDetail = copyFromRequest.detail as ClassOfTravelDeviationDetail
      detail = { detailType: DeviationDetailType.ClassOfTravel, airline: classOfTravelDetail.airline, airlineCustomName: classOfTravelDetail.airlineCustomName, originCity: classOfTravelDetail.originCity, originCityCustomName: classOfTravelDetail.originCityCustomName, originCountry: classOfTravelDetail.originCountry, destinationCity: classOfTravelDetail.destinationCity, destinationCityCustomName: classOfTravelDetail.destinationCityCustomName, destinationCountry: classOfTravelDetail.destinationCountry, classOfTravel: classOfTravelDetail.classOfTravel, flightTime: classOfTravelDetail.flightTime } as ClassOfTravelDeviationDetail;
      break;
    }
    case DeviationDetailType.Hotel: {
      const hotelDetail = copyFromRequest.detail as HotelDeviationDetail
      detail = { detailType: DeviationDetailType.Hotel, hotelName: hotelDetail.hotelName, city: hotelDetail.city, cityCustomName: hotelDetail.cityCustomName, country: hotelDetail.country } as HotelDeviationDetail;
      break;
    }
    case DeviationDetailType.AirlineExtended: {
      const airlineExtendedDetail = copyFromRequest.detail as AirlineExtendedDeviationDetail
      detail = { detailType: DeviationDetailType.AirlineExtended, airline: airlineExtendedDetail.airline, airlineCustomName: airlineExtendedDetail.airlineCustomName, originCity: airlineExtendedDetail.originCity, originCityCustomName: airlineExtendedDetail.originCityCustomName, originCountry: airlineExtendedDetail.originCountry, destinationCity: airlineExtendedDetail.destinationCity, destinationCityCustomName: airlineExtendedDetail.destinationCityCustomName, destinationCountry: airlineExtendedDetail.destinationCountry } as AirlineExtendedDeviationDetail;
      break;
    }
    default:
      throw new SystemError(`Deviation detail type ${copyFromRequest.detail.detailType} is not supported`, "Not supported");
  }
  const deviationRequestBase = {
    id: null,
    deviationType: copyFromRequest.deviationType,
    workflowType: copyFromRequest.workflowType,
    detail: detail,
    requestedBy: currentUser,
    organisationEntity: copyFromRequest.organisationEntity,
    validFrom: null,
    validTo: null,
    alternativeOptions: copyFromRequest.alternativeOptions,
    reason: copyFromRequest.reason,
    riskManagement: copyFromRequest.riskManagement,
    additionalInfo: copyFromRequest.additionalInfo,
    epra: copyFromRequest.epra,
    isSupportedByEpra: false,
    regionDirector: copyFromRequest.regionDirector,
    status: DeviationRequestStatus.Pending,
    statusChangedOn: null,
    statusChangedBy: null,
    assignedOn: null,
    assignedTo: null,
    attachments: []
  } as DeviationRequestBase;
  switch (copyFromRequest.deviationType) {
    case DeviationType.SingleInstance: {
      const singleInstanceCopyFromRequest = copyFromRequest as SingleInstanceDeviationRequest;
      const travelers = singleInstanceCopyFromRequest.travellers.map(x => ({ user: x.user, userId: null } as SystemUserWrapper));
      if (!travelers.some(x => x.user.uniqueid === currentUser.uniqueid))
        travelers.push({ user: currentUser, userId: null } as SystemUserWrapper);
      return {
        ...deviationRequestBase,
        lineManagerName: singleInstanceCopyFromRequest.lineManagerName,
        isSupportedByLineManager: false,
        travellers: travelers,
      } as SingleInstanceDeviationRequest;
    }
    case DeviationType.EntityWide: {
      const entityWideCopyFromRequest = copyFromRequest as EntityWideDeviationRequest;
      return {
        ...deviationRequestBase,
        aviationCOE: null,
        isSupportedByAviationCOE: false,
        cfo: null,
        isSupportedByCFO: false,
        regionalEntityLeader: entityWideCopyFromRequest.regionalEntityLeader,
        isSupportedByRegionalEntityLeader: false,
        seniorVPHSE: null,
        isSupportedBySeniorVPHSE: false,
        sORLeader: entityWideCopyFromRequest.sORLeader,
        isSupportedBySORLeader: false
      } as EntityWideDeviationRequest;
    }
    default:
      throw new SystemError(`Deviation type ${copyFromRequest.deviationType} is not supported`, "Not supported");
  }
}
